import {
  BankAccount,
  CounterpartyIdentity,
  CounterpartyIdentityKeyType,
  CounterpartyIdentityStatus,
  CounterpartyRole,
  CounterpartyRoleTargetType,
  getCounterpartyRoleSubjectId,
  getIdentityOfBankAccount,
  getLearnedRoleId,
  groupCounterpartyRolesBySubjectId,
  isCounterpartyRecordActive,
  isNilOrEmpty,
  TransactionLink,
} from '@rentguru/commons-utils';
import { friendlyFormatIBAN } from 'ibantools';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

const CONTACT_ROLE_TARGET_TYPES = [CounterpartyRoleTargetType.SUPPLIER, CounterpartyRoleTargetType.OWNER];

export interface CounterpartyIdentityTarget {
  id: string;
  targetType: CounterpartyRoleTargetType;
  label: string;
}

export interface CounterpartyIdentityRow {
  id: string;
  identity: CounterpartyIdentity;
  keyLabel: string;
  holderName: string | null;
  targets: CounterpartyIdentityTarget[];
  isDeclaredByBankAccount: boolean;
}

export interface CounterpartyNameResolvers {
  getLeaseName: (leaseId: string) => string | undefined;
  getUnitName: (unitId: string) => string | undefined;
  getContactName: (contactId: string) => string | undefined;
}

export const formatIbanForDisplay = (iban: string): string => friendlyFormatIBAN(iban, ' ') ?? iban;

export const formatCounterpartyIdentityKey = (identity: Pick<CounterpartyIdentity, 'keyType' | 'key'>): string =>
  identity.keyType === CounterpartyIdentityKeyType.IBAN ? formatIbanForDisplay(identity.key) : identity.key;

const getTargetName = (role: CounterpartyRole, resolvers: CounterpartyNameResolvers): string | undefined => {
  switch (role.targetType) {
    case CounterpartyRoleTargetType.LEASE:
      return resolvers.getLeaseName(role.targetId);
    case CounterpartyRoleTargetType.UNIT:
      return resolvers.getUnitName(role.targetId);
    case CounterpartyRoleTargetType.SUPPLIER:
    case CounterpartyRoleTargetType.OWNER:
      return resolvers.getContactName(role.targetId);
    default:
      return undefined;
  }
};

const getActiveTargetsOfIdentity = (
  identity: CounterpartyIdentity,
  rolesBySubjectId: Map<string, CounterpartyRole[]>,
  resolvers: CounterpartyNameResolvers
): CounterpartyIdentityTarget[] => {
  const roles = rolesBySubjectId.get(getCounterpartyRoleSubjectId(identity)) ?? [];
  const activeRoles = orderBy(
    roles.filter((role) => isCounterpartyRecordActive(role)),
    [(role) => role.evidenceCount],
    ['desc']
  );
  return activeRoles.reduce<CounterpartyIdentityTarget[]>((acc, role) => {
    const label = getTargetName(role, resolvers);
    if (!isNilOrEmpty(label)) {
      acc.push({ id: role.id, targetType: role.targetType, label });
    }
    return acc;
  }, []);
};

export const isListedCounterpartyIdentity = (identity: Pick<CounterpartyIdentity, 'status' | '_deleted'>): boolean =>
  identity.status !== CounterpartyIdentityStatus.PENDING && !identity._deleted;

// A bank account declares its identity again on every edit: deleting that identity from the register cannot last
export const getDeclaredIdentityIds = (bankAccounts: BankAccount[]): Set<string> =>
  bankAccounts.reduce<Set<string>>((acc, bankAccount) => {
    const declaredIdentity = getIdentityOfBankAccount(bankAccount);
    if (!isNil(declaredIdentity)) {
      acc.add(declaredIdentity.id);
    }
    return acc;
  }, new Set());

export const buildCounterpartyIdentityRows = (
  identities: CounterpartyIdentity[],
  roles: CounterpartyRole[],
  resolvers: CounterpartyNameResolvers,
  declaredIdentityIds: ReadonlySet<string>
): CounterpartyIdentityRow[] => {
  const rolesBySubjectId = groupCounterpartyRolesBySubjectId(roles);
  const rows = identities.reduce<CounterpartyIdentityRow[]>((acc, identity) => {
    if (!isListedCounterpartyIdentity(identity)) {
      return acc;
    }
    acc.push({
      id: identity.id,
      identity,
      keyLabel: formatCounterpartyIdentityKey(identity),
      holderName: isNil(identity.contactId) ? null : (resolvers.getContactName(identity.contactId) ?? null),
      targets: getActiveTargetsOfIdentity(identity, rolesBySubjectId, resolvers),
      isDeclaredByBankAccount: declaredIdentityIds.has(identity.id),
    });
    return acc;
  }, []);
  return orderBy(
    rows,
    [
      ({ identity }) => identity.status === CounterpartyIdentityStatus.ACTIVE,
      ({ identity }) => identity.evidenceCount,
      ({ keyLabel }) => keyLabel,
    ],
    ['desc', 'desc', 'asc']
  );
};

export const filterCounterpartyIdentityRows = (
  rows: CounterpartyIdentityRow[],
  search: string
): CounterpartyIdentityRow[] => {
  const normalizedSearch = search.trim().toLowerCase();
  if (isEmpty(normalizedSearch)) {
    return rows;
  }
  return rows.filter(({ identity, keyLabel, holderName, targets }) =>
    [keyLabel, identity.key, holderName, identity.lastCounterpartName, ...targets.map(({ label }) => label)].some(
      (text) => !isNilOrEmpty(text) && text.toLowerCase().includes(normalizedSearch)
    )
  );
};

export const isContactReferencedByCounterpartyIdentities = (
  contactId: string,
  identities: CounterpartyIdentity[],
  roles: CounterpartyRole[]
): boolean =>
  identities.some((identity) => identity.contactId === contactId && !identity._deleted) ||
  roles.some(
    (role) =>
      !role._deleted &&
      (role.subjectId === contactId ||
        (role.targetId === contactId && CONTACT_ROLE_TARGET_TYPES.includes(role.targetType)))
  );

/**
 * A user restricted to some leases sees an identity only when it is not learned from leases alone, or when one of
 * its lease roles targets a lease they can see: its IBAN and counterpart name must not leak from the others.
 */
export const filterIdentitiesVisibleOnLeases = (
  identities: CounterpartyIdentity[],
  roles: CounterpartyRole[],
  visibleLeaseIds: Set<string>
): CounterpartyIdentity[] => {
  const leaseRolesBySubjectId = groupCounterpartyRolesBySubjectId(
    roles.filter((role) => role.targetType === CounterpartyRoleTargetType.LEASE)
  );
  return identities.filter((identity) => {
    const leaseRoles = leaseRolesBySubjectId.get(getCounterpartyRoleSubjectId(identity)) ?? [];
    return isEmpty(leaseRoles) || leaseRoles.some((role) => visibleLeaseIds.has(role.targetId));
  });
};

/**
 * The roles a deleted identity takes with it: those hung on its holder, or on the orphan account itself, once no
 * other live identity holds them. Left behind, they would block the deletion of the contact and come back when the
 * account is learned again.
 */
export const getRolesDeletedWithIdentity = (
  identity: CounterpartyIdentity,
  identities: CounterpartyIdentity[],
  roles: CounterpartyRole[]
): CounterpartyRole[] => {
  const subjectId = getCounterpartyRoleSubjectId(identity);
  const isSubjectHeldElsewhere = identities.some(
    (otherIdentity) =>
      otherIdentity.id !== identity.id &&
      !otherIdentity._deleted &&
      getCounterpartyRoleSubjectId(otherIdentity) === subjectId
  );
  return isSubjectHeldElsewhere ? [] : roles.filter((role) => role.subjectId === subjectId && !role._deleted);
};

/** A decision takes back the evidence of the targets it moves the movement away from: their roles may be gone */
export const getRoleIdsOfRemovedTargets = (
  identities: Pick<CounterpartyIdentity, 'id' | 'contactId' | 'kind'>[],
  previousLinks: Pick<TransactionLink, 'linkId' | 'linkType'>[],
  keptTarget: Pick<TransactionLink, 'linkId' | 'linkType'>
): string[] => {
  const removedTargets = previousLinks.filter(
    (link) => link.linkId !== keptTarget.linkId || link.linkType !== keptTarget.linkType
  );
  return uniq(
    identities.reduce<string[]>((acc, identity) => {
      removedTargets.forEach((removedTarget) => {
        const roleId = getLearnedRoleId(identity, removedTarget);
        if (!isNil(roleId)) {
          acc.push(roleId);
        }
      });
      return acc;
    }, [])
  );
};
